import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Country, CountryInfo } from '@sharedModule/models/country.model';
import { GlobalService } from '@sharedModule/services/global.service';
import { selectCountry } from '@sharedModule/store/country/country.selector';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import {
  getSourceCountryInfoFromCountryName,
  getCountryInfoFromCountryName,
} from '@sharedModule/utils/array.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CountrySettingsService } from '../../services/country-settings.service';
import { UpsertDestCountryInterface } from '../../types/upsert-destination-country';

@Component({
  selector: 'app-upsert-destination-country',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, InputModule],
  templateUrl: './upsert-destination-country.component.html',
  styleUrl: './upsert-destination-country.component.scss',
})
export class UpsertDestinationCountryComponent implements OnInit, OnDestroy {
  public readonly attrConstant =
    ATTRIBUTES_CONSTANT.settings.countrySettings.upsertCountry;
  upsertDestCountryForm!: FormGroup;
  countryList!: Observable<Country[]>;
  sourceCountryList!: Observable<CountryInfo[]>;
  isEdit!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private globalService: GlobalService,
    private messageHandlerService: MessageHandlerService,
    private countrySettingsService: CountrySettingsService
  ) {}

  ngOnInit(): void {
    this.upsertDestCountryForm = new FormGroup({
      [this.attrConstant.srcCountryName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.srcCountryCode.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.srcCountryAlpha2Code.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.srcCurrencySupported.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.srcMobileCode.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.destCountryName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destCountryCode.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destCountryAlpha2Code.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destCurrencySupported.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destMobileCode.key]: new FormControl('', [
        Validators.required,
      ]),
    });
    this.sourceCountryList = this.store.select(selectSourceCountry);
    this.countryList = this.store.select(selectCountry);
    this.isEdit = this._isEdit(this.router.url);
  }

  onSourceCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    const selectedCountry = getSourceCountryInfoFromCountryName(
      event.value,
      event.selectOptions
    );
    if (selectedCountry)
      this.upsertDestCountryForm.patchValue({
        [this.attrConstant.srcCountryCode.key]: selectedCountry.countryCode,
        [this.attrConstant.srcCountryAlpha2Code.key]:
          selectedCountry.countryAlpha2Code,
        [this.attrConstant.srcCurrencySupported.key]:
          selectedCountry.currencySupported[0].currency,
        [this.attrConstant.srcMobileCode.key]: selectedCountry.mobileCode,
      });
  }

  onDestCountryChange(event: {
    value: string;
    selectOptions: Country[] | null;
  }): void {
    const selectedCountry = getCountryInfoFromCountryName(
      event.value,
      event.selectOptions
    );
    if (selectedCountry)
      this.upsertDestCountryForm.patchValue({
        [this.attrConstant.destCountryCode.key]: selectedCountry.alpha3,
        [this.attrConstant.destCountryAlpha2Code.key]: selectedCountry.alpha2,
        [this.attrConstant.destCurrencySupported.key]: selectedCountry.currency,
        [this.attrConstant.destMobileCode.key]: selectedCountry.telephone_code,
      });
  }

  private _isEdit = (url: string): boolean => {
    const endUrl = url.split('/');
    if (endUrl[2] == 'edit-destination-country') {
      this.patchFormValue();
      return true;
    } else {
      return false;
    }
  };

  patchFormValue(): void {
    this.globalService
      .getDataStream()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: data => {
          if (Object.keys(data).length === 0) this.redirectTo();
          else
            this.upsertDestCountryForm.patchValue({
              [this.attrConstant.srcCountryName.key]:
                data.sourceCountry.countryName,
              [this.attrConstant.srcCountryCode.key]:
                data.sourceCountry.countryCode,
              [this.attrConstant.srcCountryAlpha2Code.key]:
                data.sourceCountry.countryAlpha2Code,
              [this.attrConstant.srcCurrencySupported.key]:
                data.sourceCountry.currencySupported[0].currency,
              [this.attrConstant.srcMobileCode.key]:
                data.sourceCountry.mobileCode,
              [this.attrConstant.destCountryName.key]: data.data.countryName,
              [this.attrConstant.destCountryCode.key]: data.data.countryCode,
              [this.attrConstant.destCountryAlpha2Code.key]:
                data.data.countryAlpha2Code,
              [this.attrConstant.destCurrencySupported.key]:
                data.data.currencySupported[0].currency,
              [this.attrConstant.destMobileCode.key]: data.data.mobileCode,
            });
        },
      });
  }

  onSubmit(): void {
    if (this.upsertDestCountryForm.valid) {
      const formData = this.upsertDestCountryForm.getRawValue();
      const payload = {
        ...formData,
        srcCurrencySupported: [
          { currency: formData[this.attrConstant.srcCurrencySupported.key] },
        ],
        destCurrencySupported: [
          { currency: formData[this.attrConstant.destCurrencySupported.key] },
        ],
      };
      if (this.isEdit) this.updateDestCountry(payload);
      else this.addDestCountry(payload);
    }
  }

  addDestCountry(formValue: UpsertDestCountryInterface): void {
    this.countrySettingsService
      .addDestCountry(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.onUpsertDestCountrySuccess(x.message);
        },
      });
  }

  updateDestCountry(formValue: UpsertDestCountryInterface): void {
    this.countrySettingsService
      .updateDestCountry(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.onUpsertDestCountrySuccess(x.message);
        },
      });
  }

  onUpsertDestCountrySuccess(message: string): void {
    this.messageHandlerService.show(message);
    this.redirectTo();
  }

  redirectTo(): void {
    this.globalService.currentTabIndex = 1;
    this.router.navigate(['/settings/country']);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
